import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { searchIngredients, submitIngredient } from '../../services/ingredientService';
import { generateFromIngredients, saveGeneratedRecipe } from '../../services/recipeService';
import { useLanguage } from '../../i18n/LanguageContext';
import RecipeDetailHero from '../recipe/RecipeDetailHero';
import RecipeMetaPanel from '../recipe/RecipeMetaPanel';
import NutritionPanel from '../recipe/NutritionPanel';
import IngredientList from '../recipe/IngredientList';
import InstructionSteps from '../recipe/InstructionSteps';
import '../../pages/RecipeDetailPage.css';

const normalizeIngredient = (value) => value.trim().toLowerCase().replace(/\s+/g, ' ');

const parseIngredientsFromDraft = (draft) => {
  if (!Array.isArray(draft?.ingredients)) {
    return [];
  }

  return draft.ingredients
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item.name === 'string') {
        return [item.quantity, item.name].filter(Boolean).join(' ').trim();
      }
      return '';
    })
    .filter(Boolean);
};

const resolveErrorMessage = (error, labels) => {
  if (!error) return labels.generateUnexpectedError;
  if (error.statusCode === 401 || error.statusCode === 403) return labels.generateLoginRequired;
  if (error.statusCode === 429) return labels.generateQuotaError;
  if (error.message) return error.message;
  return labels.generateUnexpectedError;
};

const toPreviewRecipe = (draft) => {
  if (!draft) {
    return null;
  }

  const mappedIngredients = (Array.isArray(draft.ingredients) ? draft.ingredients : [])
    .map((item) => {
      if (typeof item === 'string') {
        return { name: item };
      }

      if (item && typeof item === 'object') {
        return {
          name: item.name,
          quantity: item.quantity,
        };
      }

      return null;
    })
    .filter(Boolean);

  const mappedSteps = (Array.isArray(draft.steps) ? draft.steps : [])
    .map((step) => ({ instruction: typeof step === 'string' ? step : step?.instruction }))
    .filter((step) => step.instruction);

  return {
    _id: draft.sourceRecipeId,
    id: draft.sourceRecipeId,
    title: draft.title,
    description: draft.description,
    ingredients: mappedIngredients,
    steps: mappedSteps,
    nutrition: draft.nutrition,
    lang: draft.lang,
    recipeUserId: draft.recipeUserId,
    recipeUserName: draft.recipeUserName,
    servings: draft.servings,
    prepTime: draft.prepTime,
    cookTime: draft.cookTime,
    tags: draft.tags || [],
  };
};

const GenerateRecipePanel = ({ currentUser, labels = {}, onRecipeSelect }) => {
  const { lang } = useLanguage();
  const [ingredients, setIngredients] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const debounceRef = useRef(null);
  const fetchAbortRef = useRef(null);

  const canGenerate = currentUser && ingredients.length >= 3 && !isGenerating;
  const previewRecipe = useMemo(() => toPreviewRecipe(draft), [draft]);

  const selectedSet = useMemo(() => new Set(ingredients.map(normalizeIngredient)), [ingredients]);

  const addIngredient = useCallback((rawValue, { submitToRepository = true } = {}) => {
    const value = rawValue.trim();
    if (!value) return;

    const normalized = normalizeIngredient(value);
    if (selectedSet.has(normalized)) {
      setInputValue('');
      return;
    }

    setIngredients((prev) => [...prev, value]);
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSaveMessage('');
    setError('');

    // User-entered custom ingredients are submitted so they can be promoted
    // to the shared repository after 3 unique users submit them.
    if (submitToRepository && currentUser) {
      submitIngredient(value).catch(() => {});
    }
  }, [selectedSet, currentUser]);

  const removeIngredient = useCallback((indexToRemove) => {
    setIngredients((prev) => prev.filter((_, index) => index !== indexToRemove));
    setSaveMessage('');
    setError('');
  }, []);

  const fetchSuggestions = useCallback((query) => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }

    if (!query.trim()) {
      setSuggestions([]);
      setIsLoadingSuggestions(false);
      return;
    }

    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setIsLoadingSuggestions(true);

    searchIngredients(query, controller.signal)
      .then((results) => {
        if (controller.signal.aborted) return;
        const filtered = (Array.isArray(results) ? results : []).filter((item) => {
          const name = item?.name || '';
          return name && !selectedSet.has(normalizeIngredient(name));
        });
        setSuggestions(filtered);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setSuggestions([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingSuggestions(false);
        }
      });
  }, [selectedSet]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
    };
  }, []);

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    setShowSuggestions(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(nextValue);
    }, 220);
  };

  const commitInputIfAny = () => {
    addIngredient(inputValue, { submitToRepository: true });
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitInputIfAny();
      return;
    }

    if (event.key === 'Backspace' && !inputValue.trim() && ingredients.length > 0) {
      event.preventDefault();
      removeIngredient(ingredients.length - 1);
    }
  };

  const handleGenerate = async () => {
    if (!currentUser) {
      setError(labels.generateLoginRequired);
      return;
    }

    if (ingredients.length < 3) {
      setError(labels.generateMinIngredientsError);
      return;
    }

    setError('');
    setSaveMessage('');
    setDraft(null);
    setIsGenerating(true);

    try {
      const response = await generateFromIngredients({ ingredients, lang });
      setDraft(response);
    } catch (generationError) {
      setError(resolveErrorMessage(generationError, labels));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!draft) return;
    setIsSaving(true);
    setError('');

    const payload = {
      title: draft.title,
      englishTitle: draft.englishTitle || undefined,
      ingredients: (Array.isArray(draft.ingredients) ? draft.ingredients : []).map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (!item || typeof item !== 'object') {
          return item;
        }

        const trimmedQuantity =
          item.quantity == null ? undefined : String(item.quantity).trim();

        return {
          ...item,
          quantity: trimmedQuantity || undefined,
        };
      }),
      steps: Array.isArray(draft.steps) ? draft.steps : [],
      servings: Number(draft.servings) > 0 ? Number(draft.servings) : 4,
      nutrition: draft.nutrition,
      lang: draft.lang,
      generatedByAi: !draft.isFromRepository,
      isFromRepository: Boolean(draft.isFromRepository),
      sourceRecipeId: draft.sourceRecipeId || undefined,
    };

    try {
      const saved = await saveGeneratedRecipe(payload);
      setSaveMessage(labels.generateSaveSuccess);
      if (saved && onRecipeSelect) {
        onRecipeSelect(saved);
      }
    } catch (saveError) {
      setError(saveError?.message || labels.generateSaveError);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="generate-panel" dir={labels.dir}>
      <header className="generate-panel-header">
        <h2>{labels.generateTitle}</h2>
        <p>{labels.generateDescription}</p>
      </header>

      <div className="generate-input-shell">
        <div className="generate-chip-list" aria-live="polite">
          {ingredients.map((ingredient, index) => (
            <span className="generate-chip" key={`${ingredient}-${index}`}>
              {ingredient}
              <button
                type="button"
                className="generate-chip-remove"
                onClick={() => removeIngredient(index)}
                aria-label={labels.generateRemoveIngredient}
              >
                <span className="material-symbols-rounded" aria-hidden="true">close</span>
              </button>
            </span>
          ))}
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 160)}
            onFocus={() => setShowSuggestions(true)}
            placeholder={labels.generateInputPlaceholder}
            className="generate-chip-input"
            maxLength={120}
          />
        </div>
      </div>

      {showSuggestions && (isLoadingSuggestions || suggestions.length > 0) && (
        <ul className="generate-suggestions" role="listbox">
          {isLoadingSuggestions ? (
            <li className="generate-suggestion-placeholder">{labels.generateLoadingSuggestions}</li>
          ) : (
            suggestions.map((ingredient) => (
              <li
                key={ingredient._id || ingredient.id || ingredient.name}
                role="option"
                onMouseDown={() => addIngredient(ingredient.name, { submitToRepository: false })}
              >
                {ingredient.name}
              </li>
            ))
          )}
        </ul>
      )}

      <p className="generate-hint">{labels.generateHint}</p>

      <div className="generate-actions">
        <button type="button" className="generate-submit-btn" onClick={handleGenerate} disabled={!canGenerate}>
          {isGenerating ? labels.generateSubmitting : labels.generateSubmit}
        </button>
      </div>

      {error ? <p className="generate-feedback generate-feedback--error">{error}</p> : null}
      {saveMessage ? <p className="generate-feedback generate-feedback--success">{saveMessage}</p> : null}

      {draft && previewRecipe ? (
        <article className="generate-result-card">
          <div className="generate-result-top">
            <h3>{labels.generatePreviewTitle}</h3>
            <span className={`generate-source-pill${draft.isFromRepository ? ' is-repository' : ' is-generated'}`}>
              {draft.isFromRepository ? labels.generateSourceRepository : labels.generateSourceAi}
            </span>
          </div>
          <RecipeDetailHero recipe={previewRecipe} currentUser={null} usePlaceholderImage={false} />
          <div className="recipe-detail-grid">
            <section className="recipe-detail-primary">
              <IngredientList ingredients={previewRecipe.ingredients} />
              <InstructionSteps steps={previewRecipe.steps} />
            </section>
            <aside className="recipe-detail-aside">
              <RecipeMetaPanel recipe={previewRecipe} />
              <NutritionPanel recipe={previewRecipe} />
            </aside>
          </div>

          {!draft.isFromRepository ? (
            <div className="generate-save-row">
              <button type="button" className="generate-save-btn" onClick={handleSave} disabled={isSaving}>
                {isSaving ? labels.generateSaving : labels.generateSaveButton}
              </button>
            </div>
          ) : null}
        </article>
      ) : null}
    </section>
  );
};

export default GenerateRecipePanel;
